import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { makeRESTApiRequest } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import AdvanceDateRangePicker from '../common/AdvanceDateRangePicker';
import { useAdvancedDateRangePicker } from '../../hooks/useAdvancedDateRangePicker';
import { BsBuilding } from 'react-icons/bs';
import { PersonFill } from 'react-bootstrap-icons';
import Loader from '../Loader';
import { BsSortDown, BsSortUp } from 'react-icons/bs';
import { Link } from 'react-router-dom';
import { INDIVIDUAL_ORDERS, SALES_PERSON_ROLE } from '../../helpers/constants';
import AnalyticsCardSection from '../common/OrgDashboard/AnalyticsCardSection';

export default function OrganizationSummary({ title, headerBg = '#e3f2fd', iconBg = '#2196f3', globalDateRange }) {
  const { dateRange, setDateRange, handleNavigateDate } = useAdvancedDateRangePicker();
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'latestOrder', direction: 'desc' });
  const [searchQuery, setSearchQuery] = useState('');
  const [orgAnalytics, setOrgAnalytics] = useState(null);
  const [orgAnalyticsLoading, setOrgAnalyticsLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'active', 'inactive'
  const currentUserRole = localStorage.getItem('user-role');
  useEffect(() => {
    setDateRange(globalDateRange);
  }, [globalDateRange]);

  // Table column configuration
  const tableColumns = [
    {
      key: 'organizationName',
      label: 'Organization Name',
      sortable: true,
      align: 'left',
      render: value => value
    },
    {
      key: 'latestOrder',
      label: 'Latest Order',
      sortable: true,
      align: 'center',
      render: value => (value ? moment(value).format('MMM DD, YYYY') : 'No orders')
    },
    {
      key: 'Lien Search',
      label: 'Lien Search',
      sortable: true,
      align: 'center',
      render: value => value || 0
    },
    {
      key: 'Estoppel',
      label: 'Estoppel',
      sortable: true,
      align: 'center',
      render: value => value || 0
    },
    {
      key: 'Survey',
      label: 'Survey',
      sortable: true,
      align: 'center',
      render: value => value || 0
    },
    {
      key: 'totalOrder',
      label: 'Total Orders',
      sortable: true,
      align: 'center',
      render: value => (typeof value === 'number' ? value : value || 0)
    },
    {
      key: 'clientShare',
      label: 'Client Share (%)',
      sortable: true,
      align: 'center',
      render: value => {
        const val = parseFloat(value);
        const num = isNaN(val) ? 0 : val;
        return `${num.toFixed(2)}%`;
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      align: 'center',
      render: (value, row) => {
        const isIndividualOrder = row.organizationId === INDIVIDUAL_ORDERS;
        return (
          <Link
            to={`/services/pipeline?organizationId=${row.organizationId}`}
            target="_blank"
            className={'btn btn-sm btn-outline-dark'}
            style={{
              padding: '5px',
              fontSize: '0.75rem',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              transition: 'all 0.2s ease'
            }}
          >
            {isIndividualOrder ? <PersonFill size={12} /> : <BsBuilding size={12} />}
          </Link>
        );
      }
    }
  ];

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      const startDate = dateRange.startDate ? moment(dateRange.startDate).startOf('day') : null;
      const endDate = dateRange.endDate ? moment(dateRange.endDate).endOf('day') : null;

      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.ORGANIZATION_SUMMARY,
        method: 'POST',
        requestBody: {
          dateFilter: {
            startDate: startDate?.toISOString() || null,
            endDate: endDate?.toISOString() || null
          }
        }
      });
      if (error) {
        setTableData(null);
      } else if (response && response.data) {
        setTableData(response.data);
      }
      setLoading(false);
    };
    fetchStats();
  }, [dateRange]);

  useEffect(() => {
    const fetchOrgAnalytics = async () => {
      setOrgAnalyticsLoading(true);
      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.ORG_ANALYTICS,
        method: 'GET',

      });
      if (error) {
        setOrgAnalytics(null);
      } else if (response && response.data) {
        setOrgAnalytics(response.data);
      }
      setOrgAnalyticsLoading(false);
    };
    fetchOrgAnalytics();
  }, []);

  const sortData = (data, key, direction) => {
    if (!data) return [];

    return [...data].sort((a, b) => {
      let aValue = a[key];
      let bValue = b[key];

      if (key === 'organizationName') {
        const aLabel = (aValue || '').toString().toLowerCase();
        const bLabel = (bValue || '').toString().toLowerCase();
        const cmp = aLabel.localeCompare(bLabel);
        return direction === 'asc' ? cmp : -cmp;
      }

      if (key === 'latestOrder') {
        if (!aValue && !bValue) return 0;
        if (!aValue) return direction === 'asc' ? -1 : 1;
        if (!bValue) return direction === 'asc' ? 1 : -1;
        aValue = moment(aValue).valueOf();
        bValue = moment(bValue).valueOf();
      } else if (key === 'clientShare') {
        aValue = isNaN(parseFloat(aValue)) ? 0 : parseFloat(aValue);
        bValue = isNaN(parseFloat(bValue)) ? 0 : parseFloat(bValue);
      } else if (['totalOrder', 'Estoppel', 'Lien Search', 'Survey'].includes(key)) {
        aValue = typeof aValue === 'number' ? aValue : isNaN(parseFloat(aValue)) ? 0 : parseFloat(aValue);
        bValue = typeof bValue === 'number' ? bValue : isNaN(parseFloat(bValue)) ? 0 : parseFloat(bValue);
      }

      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleSort = key => {
    //if changing the key, set the direction to desc
    if (sortConfig.key !== key) {
      setSortConfig({ key, direction: 'desc' });
      return;
    }

    const direction = sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
  };

  const handleCardClick = (filterType) => {
    setActiveFilter(prevFilter => prevFilter === filterType ? 'all' : filterType);
  };

  const filteredAndSortedData = tableData
    ? sortData(
      tableData.filter(
        row => {
          // Basic filters
          const matchesSearch = row.organizationName &&
            row.organizationName.toLowerCase().includes(searchQuery.toLowerCase());
          const notHiddenForSalesPerson = !(currentUserRole === SALES_PERSON_ROLE && row.organizationId === INDIVIDUAL_ORDERS);

          // Active/Inactive filter
          let matchesActiveFilter = true;
          if (activeFilter === 'active') {
            matchesActiveFilter = row.isActiveOrganization === true;
          } else if (activeFilter === 'inactive') {
            matchesActiveFilter = row.isActiveOrganization !== true;
          }

          return matchesSearch && notHiddenForSalesPerson && matchesActiveFilter;
        }
      ),
      sortConfig.key,
      sortConfig.direction
    )
    : [];

  return (
    <div className="mb-4">
      <div className="card border shadow-sm rounded">
        <div
          className="d-flex flex-wrap justify-content-between align-items-center mb-0 p-2 border-bottom"
          style={{ borderTopLeftRadius: '0.5rem', borderTopRightRadius: '0.5rem', background: headerBg }}
        >
          <div className="d-flex align-items-center gap-2">
            <span
              className="d-flex align-items-center justify-content-center"
              style={{ width: 24, height: 24, borderRadius: '50%', background: iconBg, marginRight: '10px' }}
            >
              <BsBuilding size={14} color="white" />
            </span>
            <h4 className="fw-bold mb-0" style={{ color: '#212529', minWidth: 180, fontSize: '1rem' }}>
              {title}
            </h4>
          </div>
          <div className="d-flex align-items-center gap-2">
            <AdvanceDateRangePicker
              value={dateRange}
              onChange={ranges => setDateRange(ranges.selection)}
              className=""
              showNavigator={true}
              rangeType={dateRange.rangeType}
              handleNavigateDate={handleNavigateDate}
            />
          </div>
        </div>

        {/* Card Body */}
        <div className="card-body d-flex flex-column p-0">
          <div className="px-3 pt-3 pb-1">
            <AnalyticsCardSection
              analyticsData={orgAnalytics}
              loading={orgAnalyticsLoading}
              onCardClick={handleCardClick}
              activeFilter={activeFilter}
            />
          </div>
          <div className="flex-grow-1 d-flex flex-column" style={{ minHeight: 0 }}>
            {loading ? (
              <div className="d-flex align-items-start justify-content-start h-100 w-100 p-3">
                <Loader />
              </div>
            ) : (
              <div className="h-100 d-flex flex-column p-3">
                <div className="mb-2 w-100">
                  <input
                    type="text"
                    className="form-control form-control-sm w-100"
                    placeholder="Search organizations..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                  />
                </div>

                <div className="flex-grow-1 border rounded" style={{ maxHeight: 450, overflowY: 'auto' }}>
                  <table className="table table-hover mb-0">
                    <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                      <tr style={{ backgroundColor: '#f8f9fa', color: '#495057', borderBottom: '2px solid #dee2e6' }}>
                        {tableColumns.map(column => (
                          <th
                            key={column.key}
                            className={column.sortable ? 'cursor-pointer' : ''}
                            onClick={column.sortable ? () => handleSort(column.key) : undefined}
                            style={{
                              cursor: column.sortable ? 'pointer' : 'default',
                              padding: '12px 16px',
                              fontWeight: '600',
                              fontSize: '0.875rem',
                              textAlign: column.align
                            }}
                          >
                            <div
                              className={`d-flex align-items-center ${column.align === 'center' ? 'justify-content-center' : ''
                                }`}
                            >
                              <span className="mr-2">{column.label}</span>
                              {column.sortable &&
                                (sortConfig.key === column.key ? (
                                  sortConfig.direction === 'asc' ? (
                                    <BsSortUp size={12} color="#dc3545" />
                                  ) : (
                                    <BsSortDown size={12} color="#dc3545" />
                                  )
                                ) : (
                                  <BsSortDown size={12} className="text-muted" />
                                ))}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAndSortedData.length > 0 ? (
                        filteredAndSortedData.map((row, index) => {
                          const isIndividualOrder = row.organizationId === INDIVIDUAL_ORDERS;
                          return (
                            <tr
                              key={row.organizationId}
                              style={{
                                borderBottom: '1px solid #f1f3f4'
                              }}
                            >
                              {tableColumns.map(column => (
                                <td
                                  key={column.key}
                                  style={{
                                    padding: '12px 16px',
                                    textAlign: column.align,
                                    fontWeight: column.key === 'organizationName' ? '500' : 'normal',
                                    color: column.key === 'organizationName' ? '#212529' : '#6c757d',
                                    fontSize: column.key === 'latestOrder' ? '0.875rem' : 'inherit'
                                  }}
                                >
                                  {column.render(row[column.key], row)}
                                  {isIndividualOrder && column.key === 'organizationName' && (
                                    <>
                                      &nbsp;
                                      <PersonFill size={15} className="mb-1" />
                                    </>
                                  )}
                                </td>
                              ))}
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={tableColumns.length} style={{ padding: '24px 16px', textAlign: 'center' }}>
                            <h6 className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                              Nothing to show
                            </h6>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
